<template>
  <div class="app-calendar overflow-hidden border">
    <div class="row no-gutters">
      <div class="col position-relative">
        <div class="card shadow-none border-0 mb-0 rounded-0">
          <div class="card-body pb-0">
            <full-calendar
                ref="refCalendar"
                :options="calendarOptions"
                class="full-calendar"
            />

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import FullCalendar from '@fullcalendar/vue'
import CalendarSidebar from './calendar-sidebar/CalendarSidebar.vue'
import CalendarEventHandler from './calendar-event-handler/CalendarEventHandler.vue'
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import {createNamespacedHelpers} from 'vuex';

const {mapState, mapActions} = createNamespacedHelpers('webinars')


export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    CalendarSidebar,
    CalendarEventHandler,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [ dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin ],
        initialView: 'timeGridWeek',
        headerToolbar: {
          start: 'sidebarToggle, prev,next, title',
          end: 'timeGridWeek,timeGridDay,listMonth',
        },
        editable: false,
        eventResizableFromStart: true,
        dragScroll: true,
        dayMaxEvents: 2,
        navLinks: true,
        eventColor: '#ffbd3b',
        events: [],
        eventClick: (params) => {
          params.jsEvent.preventDefault(); // don't let the browser navigate

          // Use arrow function to preserve 'this' context
          this.handleEventClick(params);
        },
      },
    }
  },
  computed: {
    ...mapState({
      webinars: state => state.scheduledWebinars
    }),
    eventClick(e) {
      console.log(e)
    },
    scheduledWebinars() {
      return this.webinars.map(webinar => {
        // FullCalendar requires 'start' property, not 'date'
        // Extract date from ISO datetime and combine with time
        const dateOnly = moment(webinar.scheduled_date).format('YYYY-MM-DD')
        webinar.start = dateOnly + ' ' + webinar.scheduled_time
        webinar.title = webinar?.lesson?.name

        // Add lesson room information
        webinar.hasLessonRoom = webinar.lesson_room_enabled !== false
        webinar.canJoinNow = this.canJoinWebinar(webinar)
        webinar.extendedProps = {
          webinarId: webinar.id,
          hasLessonRoom: webinar.hasLessonRoom,
          canJoinNow: webinar.canJoinNow,
          originalLink: webinar.link,
          isActive: webinar.is_past === 1, // 1 means started
          isCompleted: webinar.is_past === 2, // 2 means completed
        }

        // Keep original URL for fallback
        webinar.url = webinar.link

        return webinar
      })
    }
  },
  methods: {
    ...mapActions([
        'getWebinars',
    ]),

    async handleEventClick(params) {
      const event = params.event
      const extendedProps = event.extendedProps

      console.log('Event clicked:', event.title, extendedProps)

      // Refresh webinar data to get the latest status before checking
      await this.getWebinars()

      // Find the updated webinar from the processed data (with fixed date format)
      const updatedWebinar = this.scheduledWebinars.find(w => w.id === extendedProps.webinarId)

      if (!updatedWebinar) {
        console.error('Webinar not found after refresh')
        return
      }

      // Re-evaluate with fresh data
      const hasLessonRoom = updatedWebinar.lesson_room_enabled !== false
      const canJoinNow = this.canJoinWebinar(updatedWebinar)
      const isCompleted = updatedWebinar.is_past === 2

      console.log('Fresh webinar data:', {
        webinarId: updatedWebinar.id,
        is_past: updatedWebinar.is_past,
        status: updatedWebinar.status,
        hasLessonRoom,
        canJoinNow,
        isCompleted
      })

      // Check if this event has lesson room capability
      if (hasLessonRoom) {
        // Check if user can join the lesson room
        if (canJoinNow) {
          // Navigate to internal lesson room
          this.$router.push({
            name: 'lessonRoom',
            params: { webinarId: extendedProps.webinarId }
          })
        } else if (isCompleted) {
          // Show completed message
          this.$bvToast.toast('This lesson has already been completed.', {
            title: 'Lesson Completed',
            variant: 'info',
            solid: true
          })
        } else {
          // Show not available yet message
          this.$bvToast.toast('This lesson is not available yet. Please wait for the teacher to start the lesson.', {
            title: 'Lesson Not Available',
            variant: 'warning',
            solid: true
          })
        }
      } else {
        // Fallback to external link (original behavior)
        if (event.url) {
          window.open(event.url, '_blank')
        }
      }
    },

    canJoinWebinar(webinar) {
      // User can join if:
      // 1. Webinar is scheduled (is_past === 0) and it's within 30 minutes of start time
      // 2. Webinar is active (is_past === 1)

      console.log('canJoinWebinar called with:', {
        id: webinar.id,
        is_past: webinar.is_past,
        is_past_type: typeof webinar.is_past,
        status: webinar.status
      })

      if (webinar.is_past === 1) {
        // Webinar is currently active
        console.log('✓ Webinar is active (is_past === 1), can join!')
        return true
      }

      if (webinar.is_past === 0) {
        // Webinar is scheduled, check if it's within join window
        const now = moment()
        const webinarTime = moment(`${webinar.scheduled_date} ${webinar.scheduled_time}`)
        const minutesUntilStart = webinarTime.diff(now, 'minutes')

        console.log('Checking time window:', {
          now: now.format('YYYY-MM-DD HH:mm:ss'),
          webinarTime: webinarTime.format('YYYY-MM-DD HH:mm:ss'),
          minutesUntilStart
        })

        // Allow joining 30 minutes before and 60 minutes after scheduled time
        const canJoin = minutesUntilStart <= 30 && minutesUntilStart >= -60
        console.log(`Time window check: ${canJoin ? '✓ Can join' : '✗ Cannot join yet'}`)
        return canJoin
      }

      // Webinar is completed or cancelled
      console.log('✗ Webinar is completed/cancelled (is_past =', webinar.is_past, ')')
      return false
    },

    refreshWebinars() {
      // Refresh webinar data
      this.getWebinars()
        .then(res => {
          this.$set(this.calendarOptions, 'events', this.scheduledWebinars)
        })
    }
  },
  created() {
    this.getWebinars()
      .then(res => {
        console.log('Webinars API response:', res)
        console.log('this.webinars from state:', this.webinars)
        console.log('this.scheduledWebinars computed:', this.scheduledWebinars)
        this.$set(this.calendarOptions, 'events', this.scheduledWebinars)
        console.log('Calendar events set to:', this.calendarOptions.events)
      })
      .catch(err => {
        console.error('Failed to load webinars:', err)
      })
  },
  mounted() {

  }
}
</script>
<style>
.fc-v-event .fc-event-main{
  color: #fff !important;
  font-size: 17px;
}
</style>
<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";
</style>
